import {
  BrowserRouter,
  Navigate,
  Outlet,
  Routes,
  Route,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../hooks";
import {
  HomeScreen,
  LoginScreen,
  SupportScreen,
  ReportsScreen,
  NotFoundScreen,
  Admin,
} from "../screens";
import { LoadingFS, PermissionGuard } from "../components";
import {
  AUTH_SESSION_EXPIRED_EVENT,
  authActions,
  AuthEnum,
  AuthRolType,
} from "../context";
import { RouterEnum } from "./router.props";

const Router: React.FC = () => {
  const { dispatch, isLoggedIn: logged } = useAuth();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(true);

  useEffect(() => {
    const { setLogin, setToken, setUser, setRol, closeSession } =
      authActions(dispatch);
    const AUTH_IS_LOGGED_IN: boolean =
      localStorage.getItem(AuthEnum.LOGIN) === "true" ? true : false;

    if (AUTH_IS_LOGGED_IN) {
      const AUTH_TOKEN: string = localStorage.getItem(AuthEnum.TOKEN) ?? "";
      const AUTH_USER: string = localStorage.getItem(AuthEnum.USER) ?? "";
      const AUTH_SESSION: string = localStorage.getItem(AuthEnum.SESSION) ?? "";
      const savedRoles: string | null = localStorage.getItem(AuthEnum.ROL);
      const AUTH_ROL: string[] | undefined =
        JSON.parse(savedRoles ?? "") ?? undefined;

      const expirationDate = new Date(AUTH_SESSION);
      const isActiveSession: boolean = new Date() < expirationDate;

      if (isActiveSession) {
        setLogin(
          AUTH_IS_LOGGED_IN,
          isActiveSession,
          expirationDate.toISOString()
        );
        setToken(AUTH_TOKEN);
        setUser(AUTH_USER);
        setRol((AUTH_ROL as AuthRolType[]) ?? []);
        setIsLoggedIn(true);
      } else {
        closeSession();
        setIsLoggedIn(false);
      }
    } else {
      closeSession();
      setIsLoggedIn(false);
    }
  }, [logged]);

  // apiFetch avisa cuando el JWT no se ha podido renovar: cerramos la sesión y
  // el efecto de arriba (que depende de `logged`) redirige al login.
  useEffect(() => {
    const onSessionExpired = () => authActions(dispatch).closeSession();
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    return () =>
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [dispatch]);

  return (
    <>
      <LoadingFS />
      <Admin>
        <BrowserRouter>
          <Routes>
            <Route
              path="*"
              element={<NotFoundScreen isLoggedIn={isLoggedIn} />}
            />
            <Route
              path={RouterEnum.LOGIN}
              element={
                !isLoggedIn ? (
                  <LoginScreen />
                ) : (
                  <Navigate to={RouterEnum.DASHBOARD} />
                )
              }
            />

            {/* GRUPO DE RUTAS PRIVADAS*/}
            <Route
              element={
                isLoggedIn ? <Outlet /> : <Navigate to={RouterEnum.LOGIN} />
              }
            >
              <Route path={RouterEnum.DASHBOARD} element={<HomeScreen />} />
              <Route path={RouterEnum.REPORTS} element={<ReportsScreen />} />
              <Route path={RouterEnum.SUPPORT} element={<SupportScreen />} />

              {/* GRUPO DE RUTAS PRIVADAS - ADMIN */}
              <Route
                path={RouterEnum.ADMIN_USERS}
                element={
                  <PermissionGuard allowedRoles={["admin"]}>
                    <Admin.UsersScreen />
                  </PermissionGuard>
                }
              />
              <Route
                path={RouterEnum.ADMIN_AREA01_PRODUCTS}
                element={
                  <PermissionGuard allowedRoles={["admin"]}>
                    <Admin.Area01ProductsScreen />
                  </PermissionGuard>
                }
              />
              <Route
                path={RouterEnum.ADMIN_UNLINKED_EANS}
                element={
                  <PermissionGuard allowedRoles={["admin"]}>
                    <Admin.UnlinkedEansScreen />
                  </PermissionGuard>
                }
              />
              <Route
                path={RouterEnum.ADMIN_CARD_INVENTORY}
                element={
                  <PermissionGuard allowedRoles={["admin"]}>
                    <Admin.CardInventoryScreen />
                  </PermissionGuard>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </Admin>
    </>
  );
};

export default Router;
